import { capitalize, words } from 'lodash';
import { parseDocument } from 'yaml';

export interface ConnectionField {
  /** Dot-path key into the parsed values.yaml, also used as the Formik field name. */
  key: string;
  /** Human-readable label derived from the key. */
  label: string;
  /** Default value from the chart's values.yaml, as a string. */
  value: string;
  /**
   * Description shown as the input placeholder, taken from the chart's inline
   * "value # ..." comment or, failing that, the "# ..." comment on the line(s)
   * above the key (multi-line blocks collapsed to one line).
   */
  placeholder: string;
  /** Whether the field looks like a credential and should be masked with a reveal toggle. */
  isSecret: boolean;
  /** Whether the chart's description marks the field as "Required.", so it must be filled in. */
  required: boolean;
  /** Minimum length the chart's description declares (e.g. "min 32 chars"); undefined when none. */
  minLength?: number;
}

const SECRET_KEY_PATTERN = /key|token|secret|password/i;
const REQUIRED_DESCRIPTION_PATTERN = /\brequired\b/i;
// Matches a chart-declared minimum like "min 32 chars"; group 1 is the length.
const MIN_LENGTH_DESCRIPTION_PATTERN =
  /\b(?:min(?:imum)?|at least)\s+(\d+)\s*(?:char|character)/i;

/**
 * Turns a values.yaml key (camelCase, snake_case, or UPPER_SNAKE_CASE) into a
 * consistent Title Case label, so e.g. "OPENAI_MODEL" reads as "Openai Model"
 * rather than staying all-caps like lodash startCase would leave it.
 */
export function formatKeyLabel(key: string): string {
  return words(key).map(capitalize).join(' ');
}

interface YamlPair {
  key: unknown;
  value: unknown;
  commentBefore?: unknown;
}

interface YamlMap {
  items: YamlPair[];
}

/**
 * Flattens a chart's values.yaml into a list of editable fields — one per
 * scalar leaf, nested objects flattened with dot-separated keys (e.g.
 * "ssl.cert"). Each scalar's trailing "# ..." comment becomes the field's
 * placeholder. Whatever keys the real chart declares are what's rendered;
 * nothing here is tied to a specific chart's schema.
 */
export function parseConnectionFields(raw: string): ConnectionField[] {
  let root: unknown;
  try {
    // parseDocument (rather than parse) keeps the comment nodes so the chart's
    // inline "# ..." descriptions can be surfaced as placeholders.
    root = parseDocument(raw).contents;
  } catch {
    return [];
  }

  if (!isYamlMap(root)) {
    return [];
  }

  const fields: ConnectionField[] = [];
  flatten(root, '');
  return fields;

  function flatten(map: YamlMap, prefix: string) {
    map.items.forEach((pair) => {
      const key = String(nodeValue(pair.key) ?? '');
      const path = prefix ? `${prefix}.${key}` : key;
      const valueNode = pair.value;

      if (isYamlMap(valueNode)) {
        flatten(valueNode, path);
        return;
      }

      // Sequences/lists (e.g. imagePullSecrets: []) aren't editable as a single
      // text field, so skip them rather than rendering a garbage value.
      if (hasItems(valueNode)) {
        return;
      }

      const placeholder = fieldPlaceholder(pair);

      fields.push({
        key: path,
        label: formatKeyLabel(key),
        value: stringifyScalar(nodeValue(valueNode)),
        placeholder,
        isSecret: SECRET_KEY_PATTERN.test(key),
        required: REQUIRED_DESCRIPTION_PATTERN.test(placeholder),
        minLength: parseMinLength(placeholder),
      });
    });
  }
}

function hasItems(node: unknown): node is { items: unknown[] } {
  return (
    typeof node === 'object' &&
    node !== null &&
    'items' in node &&
    Array.isArray(node.items)
  );
}

function isYamlPair(item: unknown): item is YamlPair {
  return typeof item === 'object' && item !== null && 'key' in item;
}

// A mapping node whose items are key/value pairs, as opposed to a sequence
// whose items are bare values.
function isYamlMap(node: unknown): node is YamlMap {
  return hasItems(node) && node.items.every(isYamlPair);
}

// Unwraps a scalar AST node to its underlying value; passes plain values through.
function nodeValue(node: unknown): unknown {
  if (typeof node === 'object' && node !== null && 'value' in node) {
    return node.value;
  }
  return node;
}

// Placeholder text for a field: the trailing inline comment on the value takes
// precedence over the leading comment above the key.
function fieldPlaceholder(pair: YamlPair): string {
  return trailingComment(pair.value) || leadingComment(pair);
}

// Trailing inline comment on a value node ("value # ..."), normalized.
function trailingComment(node: unknown): string {
  if (typeof node === 'object' && node !== null && 'comment' in node) {
    return normalizeComment(node.comment);
  }
  return '';
}

// Comment on the line(s) above the key ("# ..."), normalized.
function leadingComment(pair: YamlPair): string {
  return normalizeComment(pair.commentBefore);
}

// Strips the surrounding whitespace and collapses multi-line comment blocks
// into a single line so they read as placeholder text.
function normalizeComment(comment: unknown): string {
  return typeof comment === 'string' ? comment.replace(/\s+/g, ' ').trim() : '';
}

// Reads a chart-declared minimum length from a field's description, or undefined
// when none is declared.
function parseMinLength(description: string): number | undefined {
  const match = description.match(MIN_LENGTH_DESCRIPTION_PATTERN);
  return match ? Number(match[1]) : undefined;
}

function stringifyScalar(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return typeof value === 'string' ? value : String(value);
}
